import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ITask } from 'src/modules/authentication/interfaces/task.interface';
import { ModalController } from '@ionic/angular';
import { AddTaskComponent } from '../../../company-dashboard/pages/tasks/add-task/add-task.component';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';
import { sortBy, toLower } from 'lodash';
import { HomecarePatient } from '../../interfaces/homecare-patient.interface';
import { ICarePlan } from '../../interfaces/care-plan.interface';
import { BroadcastCommonMethodsService } from 'src/modules/company-dashboard/services/broadcast-common-methods.service';

@Component({
  selector: 'app-shift-tasks',
  templateUrl: './shift-tasks.component.html',
  styleUrls: ['./shift-tasks.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShiftTasksComponent),
      multi: true
    }
  ]
})
export class ShiftTasksComponent implements ControlValueAccessor, OnChanges {
  //Selected task is used in edit shift, it contains tasks that were selected while making broadcast
  @Input() selectedTaskList;

  /**
   * Extra cssClass for the modal this component opens. The create-shift page
   * passes 'staffy-modal' to get the redesigned chrome; the screens that have
   * not been redesigned pass nothing and keep the existing look.
   */
  @Input() modalCssClass: string = '';

  /**
   * Extra cssClass for the popup this select opens. The create-shift page
   * passes 'staffy-select' for the redesigned chrome; other screens pass
   * nothing and get the standard popup.
   */
  @Input() interfaceCssClass: string = '';

  /** Options handed to the ion-select popups; rebuilt only when inputs change. */
  interfaceOptions: { cssClass: string; header?: string } = { cssClass: 'references-select' };
  carePlanInterfaceOptions: { cssClass: string; header?: string } = { cssClass: 'references-select' };

  /** Bound to the picker so it can be returned to its placeholder after a pick. */
  selectedTaskId: string;
  @Input() availableTasks: ITask[] = [];
  @Input() title : string;
  @Input() entityId: string;
  @Input() icon: string;
  @Input() control: FormControl;
  @Input() currentView: string;
  // Care plans assigned to the patient these tasks are for, and the company's other care plans.
  // Picking one replaces the tasks with the plan's
  @Input() carePlans: ICarePlan[] = [];
  @Input() otherCarePlans: ICarePlan[] = [];
  // The care plan whose tasks were filled in, if any. The dropdown shows "None" otherwise
  @Input() carePlanId: string;
  @Output() onTasksSelectionChange: EventEmitter<ITask[]> = new EventEmitter();
  // Emits the id of the care plan the user picked, or null for "None"
  @Output() onCarePlanChange: EventEmitter<string> = new EventEmitter();
  _tasks: ITask[] = [];
  // Tasks offered in the dropdown: the company's tasks that are not selected yet
  taskOptions: ITask[] = [];
  readonly noCarePlan = 'none';
  propagateChange = (_: any) => { };
  propagateTouched = (_: any) => { };

  constructor(
    private modalController: ModalController,
    private broadcastCommonMethodsService: BroadcastCommonMethodsService,
  ) {
  }

  set tasks(value: ITask[]) {
    this._tasks = value;
    this.refreshTaskOptions();
    this.onTasksSelectionChange.emit(this._tasks);
    this.propagateChange(this._tasks);
  }

  get tasks(): ITask[] {
    return this._tasks;
  }

  get selectedCarePlanId(): string {
    return this.findCarePlan(this.carePlanId) ? this.carePlanId : this.noCarePlan;
  }

  ngOnChanges(change: SimpleChanges): void {
    this.buildInterfaceOptions();

    if(!change.availableTasks || !change.availableTasks.currentValue) {
      return;
    }

    if(this.selectedTaskList && this.selectedTaskList.length) {
      this.tasks = this.selectedTaskList;
    }
    this.refreshTaskOptions();
  }

  buildInterfaceOptions() {
    this.interfaceOptions = {
      cssClass: ('references-select ' + this.interfaceCssClass).trim(),
      header: 'Select a Task',
    };
    this.carePlanInterfaceOptions = {
      cssClass: ('references-select ' + this.interfaceCssClass).trim(),
      header: 'Care plan',
    };
  }

  handleTaskSelection(taskId: string) {
    // Also fires when the control is cleared below; nothing to do then.
    if (!taskId) {
      return;
    }
    const task = this.taskOptions.find(t => t._id == taskId);
    if (!task) {
      return;
    }
    this.tasks = [...(this.tasks || []), task];
    // Return the control to its placeholder. The picked task now shows in the
    // table below, and leaving it selected implies it could be picked twice.
    this.selectedTaskId = undefined;
  }

  handleTaskRemoval(task: ITask) {
    this.tasks = this.tasks.filter(t => t._id !== task._id);
  }

  handleCarePlanSelection(carePlanId: string) {
    // ion-select also fires when the bound value is set from the parent; only a pick counts
    if ((carePlanId || this.noCarePlan) === this.selectedCarePlanId) {
      return;
    }
    const carePlan = this.findCarePlan(carePlanId);
    this.carePlanId = carePlan ? carePlan._id : null;
    this.onCarePlanChange.emit(this.carePlanId);
    // "None" empties the list so the tasks can be picked by hand
    this.tasks = this.broadcastCommonMethodsService.getCarePlanTasks(carePlan, this.availableTasks);
  }

  async showAddTaskModal(){
    const addTaskModal = await this.modalController.create({
      component: AddTaskComponent,
      cssClass: ('taskModal ' + this.modalCssClass).trim(),
      backdropDismiss: true,
      componentProps: {
        entityId: this.entityId,
      }
    });

    addTaskModal.present();
  }

  private findCarePlan(carePlanId: string): ICarePlan {
    return [...(this.carePlans || []), ...(this.otherCarePlans || [])].find(plan => plan._id === carePlanId);
  }

  private refreshTaskOptions() {
    const selectedTaskIds = (this._tasks || []).map(task => task._id);
    const unselectedTasks = (this.availableTasks || []).filter(task => !selectedTaskIds.includes(task._id));
    //sort task list by title ignoring the upper/lowercases
    this.taskOptions = sortBy(unselectedTasks, task => toLower(task.title));
  }

  //Form Accessor Functions
  writeValue(value: any): void {
    this._tasks = value || [];
    this.refreshTaskOptions();
  }


  registerOnChange(fn: (value: any) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }
}
